/**
 * assets/cart-drawer.js
 * ---------------------------------------------------------------------
 * ISHVARI WEBSITE V3 — Cart Drawer + Ajax Quantity Auto-Update
 *
 * Vanilla JS, no dependencies, loaded once, deferred, site-wide (same
 * treatment as global.js/header.js — see layout/theme.liquid). Two
 * responsibilities:
 *
 * 1. <cart-drawer> custom element — an Amazon-style side panel built
 *    entirely from Shopify's native Ajax Cart API (GET /cart.js,
 *    POST /cart/change.js). Opened by:
 *      a) the header's existing cart icon, via the [data-cart-drawer]
 *         + .open() hook assets/header.js has exposed since the
 *         header build (zero header changes required), and
 *      b) a successful Add to Cart on the Product Hero (see the small
 *         addition at the end of assets/product-hero.js).
 *    /cart (sections/main-cart.liquid) is untouched as a real page —
 *    this is an additional, faster path to the same native cart.
 *
 * 2. Ajax quantity auto-update for the /cart page itself — progressively
 *    enhances sections/main-cart.liquid's existing native form so
 *    quantity changes save via /cart/change.js as the shopper types
 *    (debounced), with no "Update cart" click required. The button
 *    itself is left in place as a true no-JS fallback; this script
 *    just makes it unnecessary when JS is available.
 *
 * Both features share one small Ajax Cart helper (below) rather than
 * duplicating fetch/format logic per surface.
 * ===================================================================== */

(function () {
  'use strict';

  /* -------------------------------------------------------------
     SHARED AJAX CART HELPERS
  ------------------------------------------------------------- */

  // Shopify's own documented money-format tokens ({{amount}},
  // {{amount_no_decimals}}, etc.), applied to the merchant's actual
  // shop.money_format string (exposed via Shopify.money_format — see
  // the small inline script in layout/theme.liquid). This guarantees
  // the drawer and the auto-updated /cart row render prices in the
  // exact same format as every server-rendered {{ price | money }}
  // elsewhere on the site — no separate/hardcoded currency symbol.
  function formatMoney(cents) {
    var format = (window.Shopify && window.Shopify.money_format) || '{{amount}}';
    var value = Number(cents) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      var negative = number < 0 ? '-' : '';
      var n = Math.abs(number).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return negative + dollars + cents2;
    }

    var value1000 = value / 100;
    var out;
    if (/amount_no_decimals_with_comma_separator/.test(format)) {
      out = withDelimiters(value1000, 0, '.', ',');
    } else if (/amount_with_comma_separator/.test(format)) {
      out = withDelimiters(value1000, 2, '.', ',');
    } else if (/amount_no_decimals/.test(format)) {
      out = withDelimiters(value1000, 0, ',', '');
    } else {
      out = withDelimiters(value1000, 2, ',', '.');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, out);
  }

  function escapeHtml(str) {
    return (str || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  function broadcastCartUpdate(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count, cart: cart } }));
  }

  /* -------------------------------------------------------------
     <cart-drawer> CUSTOM ELEMENT
  ------------------------------------------------------------- */
  if (!window.customElements || window.customElements.get('cart-drawer')) {
    // Custom elements unsupported, or already defined (e.g. hot
    // navigation) — skip redefinition rather than error.
  } else {
    (function defineCartDrawer() {
      class CartDrawerElement extends HTMLElement {
        connectedCallback() {
          this.body = this.querySelector('[data-cart-drawer-body]');
          this.loading = this.querySelector('[data-cart-drawer-loading]');
          this.footer = this.querySelector('[data-cart-drawer-footer]');
          this.subtotalEl = this.querySelector('[data-cart-drawer-subtotal]');
          this.closeTriggers = this.querySelectorAll('[data-cart-drawer-close]');
          this.lastFocused = null;
          this.debounceTimers = {};

          this.closeTriggers.forEach(function (el) {
            el.addEventListener('click', this.close.bind(this));
          }, this);

          this.onKeydown = this.onKeydown.bind(this);

          // Any other cart mutation on the page (e.g. the /cart page's
          // own auto-update, below) keeps this drawer's contents in
          // sync even if it isn't currently open.
          document.addEventListener('cart:updated', function (e) {
            if (e.detail && e.detail.cart) this.render(e.detail.cart);
          }.bind(this));
        }

        open() {
          this.hidden = false;
          this.lastFocused = document.activeElement;
          this.refresh();
          requestAnimationFrame(function () {
            this.setAttribute('data-open', 'true');
          }.bind(this));
          document.documentElement.classList.add('js-scroll-lock');
          document.addEventListener('keydown', this.onKeydown);
          var reduceMotion = window.Ishvari && window.Ishvari.animationsDisabled;
          var closeBtn = this.querySelector('[data-cart-drawer-close]');
          window.setTimeout(function () {
            if (closeBtn) closeBtn.focus();
          }, reduceMotion ? 0 : 50);
        }

        close() {
          this.removeAttribute('data-open');
          document.documentElement.classList.remove('js-scroll-lock');
          document.removeEventListener('keydown', this.onKeydown);
          var reduceMotion = window.Ishvari && window.Ishvari.animationsDisabled;
          window.setTimeout(function () {
            this.hidden = true;
          }.bind(this), reduceMotion ? 0 : 400);
          if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
        }

        onKeydown(e) {
          if (e.key === 'Escape') {
            this.close();
            return;
          }
          if (e.key !== 'Tab') return;
          var focusables = this.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (!focusables.length) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }

        refresh() {
          if (this.loading) this.loading.hidden = false;
          fetchCart()
            .then(this.render.bind(this))
            .catch(function () {
              if (this.body) {
                this.body.innerHTML = '<p class="text-body text-muted">Your cart is temporarily unavailable.</p>';
              }
            }.bind(this));
        }

        render(cart) {
          if (this.loading) this.loading.hidden = true;
          if (!this.body) return;

          if (!cart.item_count) {
            this.body.innerHTML =
              '<div class="cart-drawer__empty">' +
              '<p class="text-body-lg text-muted">Your cart is empty.</p>' +
              '<a href="' + (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'collections/all" class="btn btn-primary">Continue shopping</a>' +
              '</div>';
            if (this.footer) this.footer.hidden = true;
            return;
          }

          var html = '<ul class="cart-drawer__items" role="list">';
          cart.items.forEach(function (item) {
            var hasCompare = item.original_line_price !== item.final_line_price;
            html +=
              '<li class="cart-drawer__item" data-cart-drawer-item="' + item.key + '">' +
              '<a href="' + item.url + '" class="cart-drawer__item-media">' +
              (item.image ? '<img src="' + item.image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_240x240$1$2') + '" alt="' + escapeHtml(item.product_title) + '" width="76" height="76" loading="lazy">' : '') +
              '</a>' +
              '<div class="cart-drawer__item-content">' +
              '<a href="' + item.url + '" class="cart-drawer__item-title text-body">' + escapeHtml(item.product_title) + '</a>' +
              (item.variant_title && item.variant_title !== 'Default Title'
                ? '<p class="cart-drawer__item-variant text-caption text-muted">' + escapeHtml(item.variant_title) + '</p>'
                : '') +
              '<div class="cart-drawer__item-row">' +
              '<span class="cart-drawer__qty">' +
              '<button type="button" class="btn-icon cart-drawer__qty-btn" data-cart-drawer-qty-minus aria-label="Decrease quantity">&minus;</button>' +
              '<label class="visually-hidden" for="CartDrawerQty-' + item.key + '">Quantity</label>' +
              '<input type="number" inputmode="numeric" min="0" class="cart-drawer__qty-input" id="CartDrawerQty-' + item.key + '" value="' + item.quantity + '" data-cart-drawer-qty-input>' +
              '<button type="button" class="btn-icon cart-drawer__qty-btn" data-cart-drawer-qty-plus aria-label="Increase quantity">+</button>' +
              '</span>' +
              '<p class="cart-drawer__item-price text-small">' +
              (hasCompare ? '<span class="cart-drawer__item-price-compare">' + formatMoney(item.original_line_price) + '</span>' : '') +
              formatMoney(item.final_line_price) +
              '</p>' +
              '</div>' +
              '<button type="button" class="cart-drawer__item-remove text-caption hover-underline" data-cart-drawer-remove>Remove</button>' +
              '</div>' +
              '</li>';
          });
          html += '</ul>';
          this.body.innerHTML = html;

          if (this.footer) {
            this.footer.hidden = false;
            if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);
          }

          this.wireItemControls(cart);
        }

        wireItemControls() {
          var self = this;

          this.body.querySelectorAll('[data-cart-drawer-qty-input]').forEach(function (input) {
            input.addEventListener('input', function () {
              self.scheduleChange(input.closest('[data-cart-drawer-item]').dataset.cartDrawerItem, input.value);
            });
          });

          this.body.querySelectorAll('[data-cart-drawer-qty-minus]').forEach(function (btn) {
            btn.addEventListener('click', function () {
              var input = btn.parentElement.querySelector('[data-cart-drawer-qty-input]');
              var next = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
              input.value = next;
              self.scheduleChange(btn.closest('[data-cart-drawer-item]').dataset.cartDrawerItem, next, true);
            });
          });

          this.body.querySelectorAll('[data-cart-drawer-qty-plus]').forEach(function (btn) {
            btn.addEventListener('click', function () {
              var input = btn.parentElement.querySelector('[data-cart-drawer-qty-input]');
              var next = (parseInt(input.value, 10) || 0) + 1;
              input.value = next;
              self.scheduleChange(btn.closest('[data-cart-drawer-item]').dataset.cartDrawerItem, next, true);
            });
          });

          this.body.querySelectorAll('[data-cart-drawer-remove]').forEach(function (link) {
            link.addEventListener('click', function (e) {
              e.preventDefault();
              self.scheduleChange(link.closest('[data-cart-drawer-item]').dataset.cartDrawerItem, 0, true);
            });
          });
        }

        scheduleChange(key, quantity, immediate) {
          var self = this;
          clearTimeout(this.debounceTimers[key]);
          this.debounceTimers[key] = setTimeout(
            function () {
              changeLine(key, quantity).then(function (cart) {
                self.render(cart);
                broadcastCartUpdate(cart);
              });
            },
            immediate ? 0 : 400
          );
        }
      }

      window.customElements.define('cart-drawer', CartDrawerElement);
    })();
  }

  /* -------------------------------------------------------------
     /cart PAGE — Ajax quantity auto-update (no "Update cart" click)
     Progressively enhances sections/main-cart.liquid's native form.
     The "Update cart" button stays in the markup as a true no-JS
     fallback; this only makes clicking it unnecessary.
  ------------------------------------------------------------- */
  function initCartPageAutoUpdate() {
    var form = document.querySelector('.main-cart__form');
    if (!form || typeof window.fetch !== 'function') return;

    var subtotalEl = form.querySelector('[data-cart-subtotal]');
    var timers = {};

    function updateRow(key, cart) {
      var row = form.querySelector('[data-cart-item-key="' + key + '"]');
      var line = cart.items.filter(function (i) { return i.key === key; })[0];

      if (!line) {
        if (row) row.remove();
      } else if (row) {
        var priceEl = row.querySelector('[data-cart-item-price]');
        var input = row.querySelector('[data-cart-quantity-input]');
        if (priceEl) priceEl.textContent = formatMoney(line.final_line_price);
        if (input && Number(input.value) !== line.quantity) input.value = line.quantity;
      }

      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      if (cart.item_count === 0) {
        // Structural change (empty-state markup differs from the
        // items list) — a full reload here is the simplest correct
        // way to show it, and only happens once, when the cart
        // actually becomes empty from this page.
        window.location.reload();
      }
    }

    function schedule(key, quantity) {
      clearTimeout(timers[key]);
      timers[key] = setTimeout(function () {
        changeLine(key, quantity).then(function (cart) {
          updateRow(key, cart);
          broadcastCartUpdate(cart);
        });
      }, 500);
    }

    form.querySelectorAll('[data-cart-quantity-input]').forEach(function (input) {
      input.addEventListener('input', function () {
        schedule(input.dataset.cartLineKey, input.value);
      });
    });

    form.querySelectorAll('[data-cart-remove]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        schedule(link.dataset.cartLineKey, 0);
      });
    });

    // The native submit button remains fully functional (no-JS
    // fallback); no listener changes are needed for it to keep working.
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPageAutoUpdate);
  } else {
    initCartPageAutoUpdate();
  }
})();
